import { Form, Link, useNavigate, useSearchParams } from "@remix-run/react";
import { useState } from "react";

interface HelpDeskHeaderProps {
  title?: string;
  subtitle?: string;
  showBackLink?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export function HelpDeskHeader({
  title = "HELP CENTER",
  subtitle = "Find answers to common questions",
  showBackLink = false,
  searchQuery: controlledSearchQuery,
  onSearchChange,
}: HelpDeskHeaderProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [internalSearchQuery, setInternalSearchQuery] = useState(
    controlledSearchQuery !== undefined ? controlledSearchQuery : searchParams.get("search") || ""
  );

  const searchQuery = controlledSearchQuery !== undefined ? controlledSearchQuery : internalSearchQuery;
  const isControlled = controlledSearchQuery !== undefined;

  const handleSearchChange = (value: string) => {
    if (isControlled && onSearchChange) {
      onSearchChange(value);
    } else {
      setInternalSearchQuery(value);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isControlled) {
      const formData = new FormData(e.currentTarget);
      const query = formData.get("search") as string;
      if (query.trim()) {
        navigate(`/articles?search=${encodeURIComponent(query.trim())}`);
      } else {
        navigate("/articles");
      }
    }
  };

  return (
    <header className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row">
          <div className="flex-1">
            {showBackLink && (
              <a href="/" className="text-blue hover:text-darkBlue mb-4 inline-block">
                ← Back to Help Center
              </a>
            )}
            <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
              <Link to="/" className="flex-shrink-0">
                <img 
                  src="/logo-blue.svg" 
                  alt="Neighborcloud" 
                  className="h-16 w-auto"
                />
              </Link>
              <div className="ml-3 md:ml-0">
                <h1 className="uppercase font-bold text-booBlack tracking-widest">{title}</h1>
                <p className=" text-darkGray">{subtitle}</p>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0 w-full sm:w-64">
            <Form onSubmit={handleSearchSubmit} className="w-full sm:w-64">
              <div className="relative">
                <input
                  type="search"
                  name="search"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full px-4 py-2 pl-10 pr-4 border border-lightGray rounded-lg focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue text-body-xs"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </header>
  );
}
